import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable, InjectionToken } from '@angular/core';
import { ENVIRONMENT_PROVIDER } from '@mkp/shared/util-environment';
import { getOptionalContext } from '@mkp/core/feature-interceptor';
import { DEPRECATED_PRODUCT_CODES, Product, ProductInformation } from '@mkp/shared/models';
import { catchError, map, Observable, of } from 'rxjs';
import { ProductDto } from './product.dto';
import { ProductMapper } from '..';

export const TAXON_CODE = new InjectionToken('Taxonomy Code', {
  factory: () => 'JCAI',
});

const API_URL = new InjectionToken('API Base Url', {
  factory: () => inject(ENVIRONMENT_PROVIDER).api.ecom,
});

@Injectable({ providedIn: 'root' })
export class ProductResource {
  private readonly http = inject(HttpClient);
  private readonly taxonCode = inject(TAXON_CODE);
  private readonly apiUrl = inject(API_URL);
  private readonly defaultHeaders = new HttpHeaders({ Accept: 'application/json' });

  /**
   * `vacancyId` scopes the price ladder to a single vacancy. Products priced per
   * application quote a different ladder per vacancy, so omitting it returns a
   * variant that does not match the offer the customer was shown.
   */
  get = (productCode: string, accountId?: string, vacancyId?: string): Observable<Product> =>
    this.http
      .get<ProductDto>(
        `${this.apiUrl}/products/${productCode}?accountId=${accountId}` +
          (vacancyId ? `&vacancyId=${vacancyId}` : ''),
        { headers: this.defaultHeaders }
      )
      .pipe(map((apiProductDto) => ProductMapper.toViewModel(apiProductDto) as Product));

  getAll = (accountId?: string, vacancyId?: string): Observable<Product[]> =>
    this.fetchAllProducts(accountId, vacancyId).pipe(
      map((products) => products.map(ProductMapper.toViewModel).filter(Boolean) as Product[])
    );

  getAllAsProductInformation = (accountId?: string): Observable<ProductInformation[]> =>
    this.fetchAllProducts(accountId).pipe(
      map(
        (products) =>
          products.map(ProductMapper.toProductInformation).filter(Boolean) as ProductInformation[]
      )
    );

  private fetchAllProducts(accountId?: string, vacancyId?: string): Observable<ProductDto[]> {
    const url =
      `${this.apiUrl}/products?productTaxons.taxon.code=${this.taxonCode}` +
      (accountId ? `&accountId=${accountId}` : '') +
      (vacancyId ? `&vacancyId=${vacancyId}` : '');

    return this.http
      .get<ProductDto[]>(url, { headers: this.defaultHeaders })
      .pipe(this.filterDeprecatedProductsOperator());
  }

  getCanAccessEcomObservable(accountId: string): Observable<boolean> {
    const headers = new HttpHeaders({
      Accept: 'application/json',
      maxRetry: 1,
    });

    return this.http
      .get(`${this.apiUrl}/customers/accounts/${accountId}/verify`, {
        headers,
        context: getOptionalContext(),
      })
      .pipe(
        map(() => true),
        catchError(() => of(false))
      );
  }

  /**
   * Filters out deprecated products that should not be displayed to users.
   * This is the single point of filtering for deprecated products.
   * @see DEPRECATED_PRODUCT_CODES for the full list of deprecated products
   */
  private filterDeprecatedProductsOperator(): (
    source: Observable<ProductDto[]>
  ) => Observable<ProductDto[]> {
    return (source) =>
      source.pipe(
        map((products) =>
          products.filter(
            (product) => !(DEPRECATED_PRODUCT_CODES as readonly string[]).includes(product.code)
          )
        )
      );
  }
}
